import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs';
import { RoleSlugEnum } from 'src/app/enums/role-slug.enum';
import { UserDetailSectionEnum, UserDetailSectionEnumLabels } from 'src/app/enums/user-detail-section.enum';
import { LocationRelatableTypeEnum } from 'src/app/enums/location-relatable-type.enum';
import { User } from 'src/app/models/user.model';
import { WorkingContract } from 'src/app/models/working_contract.model';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { UserService } from 'src/app/services/user.service';
import { LocationsManagementComponent } from 'src/app/components/main/shared/locations/locations-management/locations-management.component';
import { UserDetailPersonalSectionComponent } from './user-detail-personal-section/user-detail-personal-section.component';
import { UserDetailHolidaysEmailSectionComponent } from './user-detail-holidays-email-section/user-detail-holidays-email-section.component';
import { UserDetailNotificationsSectionComponent } from './user-detail-notifications-section/user-detail-notifications-section.component';
import { UserDetailChatsSectionComponent } from './user-detail-chats-section/user-detail-chats-section.component';
import { UserDetailOauthSectionComponent } from './user-detail-oauth-section/user-detail-oauth-section.component';
import { UserDetailContractSectionComponent } from './user-detail-contract-section/user-detail-contract-section.component';

@Component({
  selector: 'app-users-detail',
  templateUrl: './users-detail.component.html',
  styleUrls: ['./users-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LocationsManagementComponent,
    UserDetailPersonalSectionComponent,
    UserDetailHolidaysEmailSectionComponent,
    UserDetailNotificationsSectionComponent,
    UserDetailChatsSectionComponent,
    UserDetailOauthSectionComponent,
    UserDetailContractSectionComponent,
  ],
})
export class UsersDetailComponent {
  protected readonly activated_route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly user_service = inject(UserService);
  private readonly employee = inject(EmployeeService);
  private readonly user_change_tick = signal(0);
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  readonly user_relatable_type = LocationRelatableTypeEnum.User;

  readonly user_detail_section_enum = UserDetailSectionEnum;
  readonly user_detail_section_enum_labels = UserDetailSectionEnumLabels;
  readonly location_relatable_type_enum = LocationRelatableTypeEnum;
  readonly user_detail_sections: UserDetailSectionEnum[] = [
    UserDetailSectionEnum.Personal,
    UserDetailSectionEnum.Locations,
    UserDetailSectionEnum.HolidaysEmail,
    UserDetailSectionEnum.Notifications,
    UserDetailSectionEnum.Chats,
    UserDetailSectionEnum.Oauth,
    UserDetailSectionEnum.Contract,
  ];
  readonly active_contract = signal<WorkingContract | null>(null);

  readonly user_id = this.resolve_user_id();
  readonly current_user = toSignal(this.user_service.currentUser, { initialValue: this.user_service.getCurrentUser() });
  private readonly route_params = toSignal(this.activated_route.paramMap, {
    initialValue: this.activated_route.snapshot.paramMap,
  });
  private readonly user_request = toSignal(
    this.api
      .getUser(this.user_id, {
        with: 'current_working_contract..current_working_contract_variable_condition..department_categories..company_product_user_member_role,current_working_contract..current_working_contract_variable_condition..department_categories..active_department_category_variable_condition,role,current_working_contract..working_contract_variable_conditions..department_categories..company_product_user_member_role,current_working_contract..working_contract_variable_conditions..department_categories..active_department_category_variable_condition,current_working_contract..current_document..document_type',
      })
      .pipe(tap((user: User): void => this.process_user_data(user))),
    { initialValue: null }
  );
  private readonly requested_section = computed((): UserDetailSectionEnum | null => {
    const section = this.route_params().get('section');

    return this.is_user_detail_section(section) ? section : null;
  });
  readonly user_loaded = computed((): boolean => {
    this.user_request();
    this.user_change_tick();
    return this.user != null;
  });
  readonly active_section = computed((): UserDetailSectionEnum => {
    const requested_section = this.requested_section();

    return requested_section != null && this.section_is_visible(requested_section) ? requested_section : UserDetailSectionEnum.Personal;
  });
  readonly active_section_label = computed((): string => this.user_detail_section_enum_labels[this.active_section()]);
  readonly profile_root_title = computed((): string => (this.router.url.includes('/users/') ? 'Editar Usuario' : 'Mi perfil'));
  readonly owns_page_content = this.router.url.includes('/my-profile');
  readonly its_me = computed((): boolean => {
    const current_user = this.current_user();

    return this.user_loaded() && current_user != null && (+this.user_id === current_user.id || this.router.url.includes('/my-profile'));
  });
  readonly is_worker_user = computed((): boolean => this.user_loaded() && this.user.role.slug === RoleSlugEnum.WORKER);
  readonly show_chats_pending_dot = computed((): boolean => this.its_me() && this.employee.hasPendingSlackChatConnections());
  readonly visible_user_detail_sections = computed((): UserDetailSectionEnum[] =>
    this.user_detail_sections.filter((section: UserDetailSectionEnum): boolean => this.section_is_visible(section))
  );
  user: User;

  constructor() {
    this.activated_route.paramMap.pipe(takeUntilDestroyed()).subscribe((): void => this.sync_section_route());
  }

  select_section(section: UserDetailSectionEnum): void {
    if (this.section_is_visible(section)) {
      void this.router.navigate(['../', section], {
        relativeTo: this.activated_route,
      });
    }
  }

  apply_updated_user(updated_user: User): void {
    if (this.its_me()) {
      const current_user = this.user_service.getCurrentUser();

      if (current_user != null) {
        this.user_service.setUser({
          ...current_user,
          ...updated_user,
        });
      }
    }

    this.user = { ...this.user, ...updated_user };
    this.active_contract.set(this.user.current_working_contract ?? null);
    this.user_change_tick.update((tick: number): number => tick + 1);
  }

  on_contract_finalized(updated_contract: WorkingContract): void {
    const contract_still_active = updated_contract.end_date == null || new Date(updated_contract.end_date).getTime() >= Date.now();

    this.active_contract.set(contract_still_active ? updated_contract : null);
    this.user_change_tick.update((tick: number): number => tick + 1);
    this.select_section(UserDetailSectionEnum.Personal);
  }

  private section_is_visible(section: UserDetailSectionEnum): boolean {
    switch (section) {
      case UserDetailSectionEnum.Personal:
        return true;
      case UserDetailSectionEnum.Locations:
        return this.is_worker_user();
      case UserDetailSectionEnum.HolidaysEmail:
        return this.its_me() && this.is_worker_user();
      case UserDetailSectionEnum.Notifications:
        return this.is_worker_user();
      case UserDetailSectionEnum.Chats:
        return (
          this.user_loaded() &&
          (this.is_worker_user() || this.user.role.slug === RoleSlugEnum.PARTNER || this.user.role.slug === RoleSlugEnum.CLIENT)
        );
      case UserDetailSectionEnum.Oauth:
        return this.its_me() && this.is_worker_user();
      case UserDetailSectionEnum.Contract:
        return this.active_contract() != null && this.is_worker_user();
      default:
        return false;
    }
  }

  private sync_section_route(): void {
    if (!this.user_loaded()) {
      return;
    }

    const active_section = this.active_section();

    if (this.route_params().get('section') === active_section) {
      return;
    }

    void this.router.navigate(['../', active_section], {
      relativeTo: this.activated_route,
      replaceUrl: true,
    });
  }

  private is_user_detail_section(section: string | null): section is UserDetailSectionEnum {
    return (
      section != null &&
      this.user_detail_sections.some((user_detail_section: UserDetailSectionEnum): boolean => user_detail_section === section)
    );
  }

  private process_user_data(user: User): void {
    this.user = user;
    this.active_contract.set(user.current_working_contract ?? null);
    this.user_change_tick.update((tick: number): number => tick + 1);
    this.sync_section_route();
  }

  private resolve_user_id(): string {
    let route: typeof this.activated_route.snapshot | null = this.activated_route.snapshot;

    while (route != null) {
      const id = route.paramMap.get('id');
      if (id != null && id !== '') {
        return id;
      }
      route = route.parent;
    }

    return `${this.user_service.getCurrentUser().id}`;
  }
}
